import { z } from "zod"

import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"
import {
  platformOperatorIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/)

export const platformProviderCodesV1 = [
  "authentik",
  "frappe",
  "stripe_billing",
  "stripe_connect",
  "worklio",
  "mapbox",
  "property_data",
  "quo",
  "twilio",
  "email",
  "object_storage",
  "dns_tls",
  "analytics",
  "observability",
] as const

export const platformProviderCodeV1Schema = z.enum(platformProviderCodesV1)

export const platformProviderOwnerCapabilitiesV1 = {
  authentik: "identity",
  frappe: "tenant.operations",
  stripe_billing: "platform.billing",
  stripe_connect: "customer.payments",
  worklio: "payroll",
  mapbox: "mapping",
  property_data: "property.data",
  quo: "communications",
  twilio: "communications",
  email: "communications",
  object_storage: "media",
  dns_tls: "domain.lifecycle",
  analytics: "analytics",
  observability: "observability",
} as const

export const platformProviderEnvironmentV1Schema = z.enum([
  "development",
  "internal",
  "canary",
  "production",
])

export const platformProviderHealthV1Schema = z.enum([
  "healthy",
  "degraded",
  "failed",
  "unknown",
  "not_applicable",
])

export const platformProviderFailureCodeV1Schema = z.enum([
  "authentication_failed",
  "authorization_failed",
  "configuration_invalid",
  "credential_rotation_overdue",
  "provider_unavailable",
  "quota_exhausted",
  "quota_near_limit",
  "reconciliation_required",
  "webhook_signature_invalid",
  "webhook_unreachable",
])

const providerIntegrationInputShape = {
  contractVersion: z.literal(1),
  integrationId: z.uuid(),
  providerCode: platformProviderCodeV1Schema,
  owningCapability: codeSchema,
  credentialScope: z.enum(["platform", "tenant"]),
  targetTenantId: tenantIdSchema.nullable(),
  environment: platformProviderEnvironmentV1Schema,
  accountableOwner: codeSchema,
  approvedScopes: z.array(codeSchema).min(1).max(64),
  secretReferenceId: z.uuid(),
  rotationDueAt: timestampSchema,
  webhookHealth: platformProviderHealthV1Schema,
  quotaHealth: platformProviderHealthV1Schema,
  reconciliationHealth: platformProviderHealthV1Schema,
  safeLastSuccessAt: timestampSchema.nullable(),
  failureCode: platformProviderFailureCodeV1Schema.nullable(),
  runbookRef: z.string().regex(/^runbook:\/\/[a-z0-9][a-z0-9/_.-]{2,191}$/),
} as const

const validateIntegration = (
  integration: {
    providerCode: keyof typeof platformProviderOwnerCapabilitiesV1
    owningCapability: string
    credentialScope: "platform" | "tenant"
    targetTenantId: string | null
    approvedScopes: readonly string[]
    webhookHealth: z.infer<typeof platformProviderHealthV1Schema>
    quotaHealth: z.infer<typeof platformProviderHealthV1Schema>
    reconciliationHealth: z.infer<typeof platformProviderHealthV1Schema>
    failureCode: z.infer<typeof platformProviderFailureCodeV1Schema> | null
  },
  context: z.RefinementCtx,
) => {
  if (
    integration.owningCapability
      !== platformProviderOwnerCapabilitiesV1[integration.providerCode]
  ) {
    context.addIssue({
      code: "custom",
      path: ["owningCapability"],
      message: "Provider must remain behind its product-owned Capability.",
    })
  }
  if (
    (integration.credentialScope === "platform"
      && integration.targetTenantId !== null)
    || (integration.credentialScope === "tenant"
      && integration.targetTenantId === null)
  ) {
    context.addIssue({
      code: "custom",
      path: ["targetTenantId"],
      message: "Credential scope and Tenant target must agree.",
    })
  }
  if (
    integration.providerCode === "stripe_billing"
    && integration.credentialScope !== "platform"
  ) {
    context.addIssue({
      code: "custom",
      path: ["credentialScope"],
      message: "Stripe Billing credentials are platform-scoped.",
    })
  }
  if (
    ["frappe", "stripe_connect", "worklio", "mapbox", "property_data"]
      .includes(integration.providerCode)
    && integration.credentialScope !== "tenant"
  ) {
    context.addIssue({
      code: "custom",
      path: ["credentialScope"],
      message: "This provider integration is Tenant-scoped.",
    })
  }
  if (new Set(integration.approvedScopes).size !== integration.approvedScopes.length) {
    context.addIssue({
      code: "custom",
      path: ["approvedScopes"],
      message: "Approved provider scopes must be unique.",
    })
  }
  const health = [
    integration.webhookHealth,
    integration.quotaHealth,
    integration.reconciliationHealth,
  ]
  if (
    integration.failureCode === null
    && health.some((value) => value === "failed")
  ) {
    context.addIssue({
      code: "custom",
      path: ["failureCode"],
      message: "Failed provider health requires a stable failure code.",
    })
  }
}

export const platformProviderIntegrationInputV1Schema = z
  .object(providerIntegrationInputShape)
  .strict()
  .superRefine(validateIntegration)

export const platformProviderIntegrationV1Schema = z
  .object({
    ...providerIntegrationInputShape,
    version: z.number().int().positive(),
    updatedAt: timestampSchema,
  })
  .strict()
  .superRefine(validateIntegration)

export const upsertPlatformProviderIntegrationV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema,
  integration: platformProviderIntegrationInputV1Schema,
}).strict()

export const platformProviderReadinessV1Schema = z.enum([
  "ready",
  "degraded",
  "action_required",
  "unknown",
])

export const platformProviderIntegrationProjectionV1Schema = z.object({
  ...providerIntegrationInputShape,
  version: z.number().int().positive(),
  updatedAt: timestampSchema,
  readiness: platformProviderReadinessV1Schema,
  rotationOverdue: z.boolean(),
  redacted: z.literal(true),
}).strict().superRefine(validateIntegration)

export const listPlatformProviderIntegrationsV1Schema = z.object({
  platformOperatorId: platformOperatorIdSchema,
  environment: platformProviderEnvironmentV1Schema.optional(),
  providerCode: platformProviderCodeV1Schema.optional(),
  credentialScope: z.enum(["platform", "tenant"]).optional(),
  targetTenantId: tenantIdSchema.optional(),
  asOf: timestampSchema,
  limit: z.number().int().min(1).max(200).default(50),
  cursor: z.uuid().nullable().default(null),
}).strict()

export const platformProviderIntegrationPageV1Schema = z.object({
  responseVersion: z.literal(1),
  items: z.array(platformProviderIntegrationProjectionV1Schema).max(200),
  nextCursor: z.uuid().nullable(),
}).strict()

export type PlatformProviderCodeV1 = z.infer<
  typeof platformProviderCodeV1Schema
>
export type PlatformProviderEnvironmentV1 = z.infer<
  typeof platformProviderEnvironmentV1Schema
>
export type PlatformProviderIntegrationInputV1 = z.infer<
  typeof platformProviderIntegrationInputV1Schema
>
export type PlatformProviderIntegrationV1 = z.infer<
  typeof platformProviderIntegrationV1Schema
>
export type PlatformProviderIntegrationProjectionV1 = z.infer<
  typeof platformProviderIntegrationProjectionV1Schema
>
export type ListPlatformProviderIntegrationsV1 = z.infer<
  typeof listPlatformProviderIntegrationsV1Schema
>
export type PlatformProviderIntegrationPageV1 = z.infer<
  typeof platformProviderIntegrationPageV1Schema
>
export type UpsertPlatformProviderIntegrationV1 = z.infer<
  typeof upsertPlatformProviderIntegrationV1Schema
>
